import { useNavigate } from 'react-router-dom';

export interface ErrorResponse {
    code?: string;
    message?: string;
    status?: number;
    details?: any;
}

export class ErrorInterceptor {
    private static instance: ErrorInterceptor;
    private navigate: ((path: string) => void) | null = null;

    private constructor() { }

    static getInstance(): ErrorInterceptor {
        if (!ErrorInterceptor.instance) {
            ErrorInterceptor.instance = new ErrorInterceptor();
        }
        return ErrorInterceptor.instance;
    }

    setNavigate(navigate: (path: string) => void) {
        this.navigate = navigate;
    }

    /**
 * Handle authentication errors and redirect to login if needed
 */
    handleError(error: any): void {
        console.error('[ErrorInterceptor] Handling error:', error);
        console.log('[ErrorInterceptor] Error details:', {
            code: error?.code,
            message: error?.message,
            name: error?.name,
            stack: error?.stack?.split('\n')[0]
        });

        // Check if it's a network error that shouldn't trigger redirects
        if (this.isNetworkError(error)) {
            console.log('[ErrorInterceptor] Network error detected, not redirecting to login');
            return;
        }

        // Check if it's an authentication error
        if (this.isAuthenticationError(error)) {
            console.log('[ErrorInterceptor] Authentication error detected, redirecting to login');
            this.handleAuthenticationError(error);
            return;
        }

        // Check if it's a session expiration error
        if (this.isSessionExpiredError(error)) {
            console.log('[ErrorInterceptor] Session expired, redirecting to login');
            this.handleSessionExpiration();
            return;
        }

        // For other errors, just log them
        console.error('[ErrorInterceptor] Non-authentication error:', error);
    }

    /**
 * Check if the error is an authentication error
 */
    private isAuthenticationError(error: any): boolean {
        if (!error) return false;

        // Firebase Auth errors
        if (error.code) {
            const authErrorCodes = [
                'auth/user-not-found',
                'auth/wrong-password',
                'auth/invalid-credential',
                'auth/user-disabled',
                'auth/too-many-requests',
                'auth/operation-not-allowed',
                'auth/invalid-email',
                'auth/weak-password',
                'auth/email-already-in-use',
                'auth/requires-recent-login',
                'auth/credential-already-in-use'
            ];

            // Exclude network-related errors that shouldn't trigger login redirect
            const networkErrorCodes = [
                'auth/network-request-failed',
                'auth/too-many-requests'
            ];

            if (networkErrorCodes.includes(error.code)) {
                return false;
            }

            return authErrorCodes.includes(error.code);
        }

        // HTTP status codes
        if (error.status) {
            return error.status === 401 || error.status === 403;
        }

        // Error messages
        if (error.message) {
            const authErrorMessages = [
                'unauthenticated',
                'unauthorized',
                'authentication failed',
                'invalid token',
                'token expired',
                'session expired',
                'user must be authenticated'
            ];
            return authErrorMessages.some(msg =>
                error.message.toLowerCase().includes(msg.toLowerCase())
            );
        }

        return false;
    }

    /**
 * Check if the error is a network error that shouldn't trigger redirects
 */
    private isNetworkError(error: any): boolean {
        if (!error) return false;

        // Check for network-related error codes
        if (error.code) {
            const networkErrorCodes = [
                'auth/network-request-failed',
                'auth/too-many-requests'
            ];

            // Log for debugging
            if (networkErrorCodes.includes(error.code)) {
                console.log('[ErrorInterceptor] Network error detected by code:', error.code);
                return true;
            }
        }

        // Check for network-related error messages
        if (error.message) {
            const networkErrorMessages = [
                'network request failed',
                'too many requests',
                'illegal invocation',
                'fetch error'
            ];

            const isNetworkError = networkErrorMessages.some(msg =>
                error.message.toLowerCase().includes(msg.toLowerCase())
            );

            if (isNetworkError) {
                console.log('[ErrorInterceptor] Network error detected by message:', error.message);
                return true;
            }
        }

        // Check if it's a Firebase network error
        if (error.code === 'auth/network-request-failed') {
            console.log('[ErrorInterceptor] Firebase network error detected:', error.code);
            return true;
        }

        // Additional check for the specific error you're seeing
        if (error.code && error.code.includes('network-request-failed')) {
            console.log('[ErrorInterceptor] Network request failed error detected:', error.code);
            return true;
        }

        return false;
    }

    /**
     * Check if the error indicates session expiration
     */
    private isSessionExpiredError(error: any): boolean {
        if (!error) return false;

        // Check for specific session expiration indicators
        if (error.message) {
            const sessionExpiredMessages = [
                'session expired',
                'token expired',
                'authentication expired',
                'login required',
                'please log in again'
            ];
            return sessionExpiredMessages.some(msg =>
                error.message.toLowerCase().includes(msg.toLowerCase())
            );
        }

        // Check for Firebase token expiration
        if (error.code === 'auth/user-token-expired' || error.code === 'auth/user-not-found') {
            return true;
        }

        return false;
    }

    /**
     * Handle authentication errors by redirecting to login
     */
    private handleAuthenticationError(error: any): void {
        // Check if we're already on a login page - if so, don't redirect
        // This allows login pages to handle errors inline
        if (typeof window !== 'undefined' && window.location) {
            const currentPath = window.location.pathname;
            const isLoginPage = currentPath === '/login' || 
                               currentPath === '/admin/login' || 
                               currentPath === '/subscription/login' ||
                               currentPath.startsWith('/login') ||
                               currentPath.startsWith('/admin/login') ||
                               currentPath.startsWith('/subscription/login');
            
            if (isLoginPage) {
                console.log('[ErrorInterceptor] Already on login page, not redirecting. Let the page handle the error inline.');
                return;
            }
        }

        // Clear any existing authentication data
        this.clearAuthenticationData();

        // Redirect to login
        if (this.navigate) {
            this.navigate('/login');
        } else {
            // Fallback to window.location if navigate is not available
            window.location.href = '/login';
        }
    }

    /**
     * Handle session expiration by clearing data and redirecting
     */
    private handleSessionExpiration(): void {
        // Check if we're already on a login page - if so, don't redirect
        if (typeof window !== 'undefined' && window.location) {
            const currentPath = window.location.pathname;
            const isLoginPage = currentPath === '/login' || 
                               currentPath === '/admin/login' || 
                               currentPath === '/subscription/login' ||
                               currentPath.startsWith('/login') ||
                               currentPath.startsWith('/admin/login') ||
                               currentPath.startsWith('/subscription/login');
            
            if (isLoginPage) {
                console.log('[ErrorInterceptor] Already on login page, not redirecting for session expiration.');
                return;
            }
        }

        // Clear authentication data
        this.clearAuthenticationData();

        // Show a brief message to the user
        this.showSessionExpiredMessage();

        // Redirect to login
        if (this.navigate) {
            this.navigate('/login');
        } else {
            window.location.href = '/login';
        }
    }

    /**
     * Clear all authentication-related data
     */
    private clearAuthenticationData(): void {
        try {
            // Clear sessionStorage
            if (typeof sessionStorage !== 'undefined') {
                sessionStorage.clear();
            }

            // Clear localStorage Firebase auth data
            if (typeof localStorage !== 'undefined') {
                const firebaseKeys = Object.keys(localStorage).filter(key =>
                    key.startsWith('firebase:authUser:') ||
                    key.startsWith('firebase:') ||
                    key.includes('firebase')
                );
                firebaseKeys.forEach(key => localStorage.removeItem(key));
            }

            // Clear Azure AD tokens
            if (typeof sessionStorage !== 'undefined') {
                sessionStorage.removeItem('azuread.tokens');
                sessionStorage.removeItem('auth.azure.callback.inprogress');
                sessionStorage.removeItem('auth.provider.switch.inprogress');
                sessionStorage.removeItem('auth.google.signin.inprogress');
                sessionStorage.removeItem('auth.user.role');
            }

            console.log('[ErrorInterceptor] Authentication data cleared');
        } catch (error) {
            console.error('[ErrorInterceptor] Error clearing authentication data:', error);
        }
    }

    /**
     * Show a brief message about session expiration
     */
    private showSessionExpiredMessage(): void {
        // Create a temporary notification element
        const notification = document.createElement('div');
        notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #ef4444;
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      z-index: 9999;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
    `;
        notification.textContent = 'Session expired. Please log in again.';

        document.body.appendChild(notification);

        // Remove after 3 seconds
        setTimeout(() => {
            if (document.body.contains(notification)) {
                document.body.removeChild(notification);
            }
        }, 3000);
    }
}

// Export singleton instance
export const errorInterceptor = ErrorInterceptor.getInstance();
